import { Download, Visibility } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import { S3Service } from "../../../Utilities/S3Service";
import { monthFormatter } from "../Bill/SingleBill";
import { numberFormatter } from "../Bill/Cells/Last12Summary";
import { downloadReadingImageFile } from "./Reading";

type ReadingImageCardProps = {
    reading: any;
    onView: () => void;
};

export const ReadingImageCard = ({ reading, onView }: ReadingImageCardProps) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [hasImage, setHasImage] = useState(false);

    const meterNo = reading.meter?.meterNo ?? "—";
    const billingMonth = monthFormatter.format(
        new Date(reading.year ?? 0, (reading.month ?? 1) - 1, 1)
    );
    const currentUnits =
        reading.currentUnits != null
            ? numberFormatter.format(reading.currentUnits)
            : "—";

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        const loadImage = async () => {
            setLoading(true);
            setHasImage(false);
            setImageUrl(null);

            const readingKey = `readings/${reading.id}`;
            const [data, err] = await S3Service.getS3ByKey(readingKey);

            if (!data || err || cancelled) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(data.url);
                if (response.status !== 200 || cancelled) {
                    setLoading(false);
                    return;
                }

                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);

                if (!cancelled) {
                    setImageUrl(objectUrl);
                    setHasImage(true);
                }
            } catch {
                if (!cancelled) {
                    setHasImage(false);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [reading.id]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadReadingImageFile(reading);
        } catch (error) {
            console.error("Error downloading image:", error);
            alert("Error downloading image.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Card
            elevation={1}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <CardContent sx={{ pb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {meterNo}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {billingMonth}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    Curr. Units: {currentUnits}
                </Typography>
            </CardContent>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                    mx: 2,
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                {loading ? (
                    <CircularProgress size={32} />
                ) : hasImage && imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={`Reading ${meterNo}`}
                        sx={{
                            width: "100%",
                            height: "100%",
                            maxHeight: 220,
                            objectFit: "contain",
                            cursor: "pointer",
                        }}
                        onClick={onView}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" p={2}>
                        No image uploaded
                    </Typography>
                )}
            </Box>

            <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={onView}
                    disabled={!hasImage}
                >
                    View
                </Button>

                <MultiPermissionAuthorize ops={["DOWNLOAD READING_IMAGE"]}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            downloading ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Download />
                            )
                        }
                        onClick={handleDownload}
                        disabled={!hasImage || downloading}
                    >
                        Download
                    </Button>
                </MultiPermissionAuthorize>
            </CardActions>
        </Card>
    );
};
